import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GridApi, ColDef } from 'ag-grid-community';
import { Subject, interval, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { SupportService } from '../shared/support.service';
import { NotificationService } from '../../shared/services/notification.service';
import {
  AutocloseTicket,
  AutoModeState,
  CaseChangeHistoryEntry,
  ClosedManualTicket,
  formatSupportCaseNames,
  FreshdeskConversation,
  getSupportCaseNames,
  ManualTicket,
  PromptConfig,
  SupportCase,
  SupportDecision,
  TicketDetail,
} from '../shared/support.model';

@Component({
  selector: 'app-support-index',
  templateUrl: './support-index.component.html',
  styleUrls: ['./support-index.component.scss'],
  standalone: false,
})
export class SupportIndexComponent implements OnInit, OnDestroy {
  readonly formatSupportCaseNames = formatSupportCaseNames;

  activeTab: 'manual' | 'closed' | 'autoclose' | 'instructions' = 'manual';

  // ─── Manual Tickets ──────────────────────────────────────────────
  manualTickets: ManualTicket[] = [];
  manualLoading = false;
  autoModeState: AutoModeState = { enabled: false, manualCount: 0 };
  ticketActionsInFlight = new Set<number>();
  autoModeToggleInFlight = false;

  // ─── Closed Manual Tickets ─────────────────────────────────────
  closedTickets: ClosedManualTicket[] = [];
  closedLoading = false;
  closedDays = 7;
  closedPage = 1;
  closedPageSize = 20;
  closedTotal = 0;

  // ─── Autoclose Tickets ──────────────────────────────────────────
  autocloseTickets: AutocloseTicket[] = [];
  autocloseLoading = false;
  autocloseDays = 7;
  autoclosePage = 1;
  autoclosePageSize = 20;
  autocloseTotal = 0;

  // ─── Instructions ───────────────────────────────────────────────
  cases: SupportCase[] = [];
  casesLoading = false;

  // ─── Prompt Config ────────────────────────────────────────────
  promptConfigs: PromptConfig[] = [];
  promptConfigLoading = false;
  promptConfigSaving = new Set<string>();

  // ─── Case Form ──────────────────────────────────────────────────
  caseForm: FormGroup;
  editingCase: SupportCase | null = null;
  showCaseForm = false;
  @ViewChild('instructionsTextarea') instructionsTextarea: ElementRef<HTMLTextAreaElement>;

  // ─── Case History ───────────────────────────────────────────────
  caseHistory: CaseChangeHistoryEntry[] = [];
  showCaseHistory = false;
  caseHistoryName = '';

  // ─── Ticket Detail ──────────────────────────────────────────────
  ticketDetail: TicketDetail | null = null;
  showTicketDetail = false;
  ticketDetailLoading = false;
  ticketDetailTab: 'conversation' | 'decision' | 'data' = 'conversation';
  ticketDetailIsManual = false;
  cachedReversedConversations: FreshdeskConversation[] = [];

  // ─── Reply Counts ───────────────────────────────────────────────
  ticketReplyCounts = new Map<number, number>();
  private fetchedReplyCountIds = new Set<number>();

  // ─── Sort State ─────────────────────────────────────────────────
  manualSort: { field: string | null; dir: 'asc' | 'desc' } = { field: null, dir: 'asc' };
  closedSort: { field: string | null; dir: 'asc' | 'desc' } = { field: null, dir: 'asc' };
  autocloseSort: { field: string | null; dir: 'asc' | 'desc' } = { field: null, dir: 'asc' };

  toggleManualSort(field: string) {
    this.toggleSort(this.manualSort, field);
    this.recomputeFilteredTickets();
  }
  toggleClosedSort(field: string) {
    this.toggleSort(this.closedSort, field);
    this.recomputeFilteredTickets();
  }
  toggleAutocloseSort(field: string) {
    this.toggleSort(this.autocloseSort, field);
    this.recomputeFilteredTickets();
  }

  private toggleSort(sort: { field: string | null; dir: 'asc' | 'desc' }, field: string) {
    if (sort.field !== field) {
      sort.field = field;
      sort.dir = 'asc';
    } else if (sort.dir === 'asc') {
      sort.dir = 'desc';
    } else {
      sort.field = null;
      sort.dir = 'asc';
    }
  }

  sortIcon(sort: { field: string | null; dir: 'asc' | 'desc' }, field: string): string {
    if (sort.field !== field) return 'fas fa-sort text-muted ms-5';
    return sort.dir === 'asc' ? 'fas fa-sort-up ms-5' : 'fas fa-sort-down ms-5';
  }

  private applySortTo<T>(items: T[], sort: { field: string | null; dir: 'asc' | 'desc' }): T[] {
    if (!sort.field) return items;
    const { field, dir } = sort;
    return [...items].sort((a: any, b: any) => {
      let av: any, bv: any;
      if (field === 'phoneNumber') {
        av = a.decision?.phoneNumber || '';
        bv = b.decision?.phoneNumber || '';
      } else if (field === 'replies') {
        // -1 so tickets whose count hasn't loaded yet sort last
        av = this.ticketReplyCounts.get(a.ticketId) ?? -1;
        bv = this.ticketReplyCounts.get(b.ticketId) ?? -1;
      } else {
        av = a[field] ?? 0;
        bv = b[field] ?? 0;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return dir === 'asc' ? cmp : -cmp;
    });
  }

  // ─── Search & Filters ───────────────────────────────────────────
  ticketSearch = '';
  selectedCaseNames = new Set<string>();
  filteredManualTickets: ManualTicket[] = [];
  filteredClosedTickets: ClosedManualTicket[] = [];
  filteredAutocloseTickets: AutocloseTicket[] = [];

  get isCaseFilterActive(): boolean {
    return this.selectedCaseNames.size > 0;
  }

  toggleCaseFilter(caseName: string) {
    if (this.selectedCaseNames.has(caseName)) {
      this.selectedCaseNames.delete(caseName);
    } else {
      this.selectedCaseNames.add(caseName);
    }
    this.selectedCaseNames = new Set(this.selectedCaseNames);
    this.recomputeFilteredTickets();
  }

  clearCaseFilter() {
    this.selectedCaseNames = new Set();
    this.recomputeFilteredTickets();
  }

  get casesForFilter(): SupportCase[] {
    return this.cases.filter(c => c.name !== 'For each case user data included in prompt');
  }

  recomputeFilteredTickets() {
    const q = this.ticketSearch.trim();
    const hasCaseFilter = this.selectedCaseNames.size > 0;
    const matchesQuery = (ticketId: number, decision: { phoneNumber?: string | null; emailLinkedPhoneNumber?: string | null } | null) => {
      if (!q) return true;
      if (String(ticketId).includes(q)) return true;
      if (decision?.phoneNumber?.includes(q)) return true;
      if (decision?.emailLinkedPhoneNumber?.includes(q)) return true;
      return false;
    };
    this.filteredManualTickets = this.applySortTo(
      this.manualTickets.filter(t => {
        if (!matchesQuery(t.ticketId, t.decision)) return false;
        if (hasCaseFilter && !this.hasSelectedSupportCase(t.decision)) return false;
        return true;
      }),
      this.manualSort
    );
    this.filteredClosedTickets = this.applySortTo(
      this.closedTickets.filter(t => {
        if (!matchesQuery(t.ticketId, t.decision)) return false;
        if (hasCaseFilter && !this.hasSelectedSupportCase(t.decision)) return false;
        return true;
      }),
      this.closedSort
    );
    this.filteredAutocloseTickets = this.applySortTo(
      this.autocloseTickets.filter(t => {
        if (!matchesQuery(t.ticketId, t.decision)) return false;
        if (hasCaseFilter && !this.hasSelectedSupportCase(t.decision)) return false;
        return true;
      }),
      this.autocloseSort
    );
  }

  private hasSelectedSupportCase(decision: SupportDecision | null | undefined): boolean {
    return getSupportCaseNames(decision).some(caseName => this.selectedCaseNames.has(caseName));
  }

  // ─── Edit Reply ───────────────────────────────────────────────
  editingReply = false;
  editedReplyText = '';
  replyDisplayHeight = 120;
  feedbackExpanded = false;
  reviewRevisionComment = '';
  reviewRevisionSaving = false;

  private destroy$ = new Subject<void>();
  private pollSub: Subscription | null = null;

  constructor(
    private supportService: SupportService,
    private notification: NotificationService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title
  ) {
    this.caseForm = this.fb.group({
      name: ['', Validators.required],
      instructions: ['', Validators.required],
      autoclose: [false],
      sortOrder: [0],
    });
  }

  ngOnInit() {
    this.titleService.setTitle('Support');
    this.loadManualTickets();
    this.loadAutoModeState();
    this.loadCases();
    this.startPolling();

    const ticketParam = this.route.snapshot.queryParamMap.get('ticket');
    if (ticketParam) {
      const ticketId = Number(ticketParam);
      if (Number.isInteger(ticketId) && ticketId > 0) {
        this.openTicketDetail(ticketId);
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
  }

  // ─── Tab switching ──────────────────────────────────────────────

  switchTab(tab: 'manual' | 'closed' | 'autoclose' | 'instructions') {
    this.activeTab = tab;
    if (tab === 'closed' && this.closedTickets.length === 0) {
      this.loadClosedTickets();
    }
    if (tab === 'autoclose' && this.autocloseTickets.length === 0) {
      this.loadAutocloseTickets();
    }
    if (tab === 'instructions') {
      if (this.cases.length === 0) {
        this.loadCases();
      }
      if (this.promptConfigs.length === 0) {
        this.loadPromptConfig();
      }
    }
  }

  // ─── Polling ────────────────────────────────────────────────────

  private startPolling() {
    this.pollSub = interval(30000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.activeTab === 'manual') {
          this.loadManualTickets();
          this.loadAutoModeState();
        }
      });
  }

  private stopPolling() {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
  }

  // ─── Manual Tickets ─────────────────────────────────────────────

  loadManualTickets() {
    if (this.manualTickets.length === 0) {
      this.manualLoading = true;
    }
    this.supportService.getManualTickets().subscribe({
      next: res => {
        this.mergeManualTickets(res.items);
        this.manualLoading = false;
        this.recomputeFilteredTickets();
        this.fetchMissingReplyCounts();
      },
      error: () => {
        this.notification.error('Failed to load manual tickets');
        this.manualLoading = false;
      },
    });
  }

  private fetchMissingReplyCounts() {
    const currentIds = new Set(this.manualTickets.map(t => t.ticketId));
    // Remove counts for tickets no longer in queue
    for (const id of [...this.fetchedReplyCountIds]) {
      if (!currentIds.has(id)) {
        this.fetchedReplyCountIds.delete(id);
        this.ticketReplyCounts.delete(id);
      }
    }
    // Fetch counts for newly added tickets
    for (const ticket of this.manualTickets) {
      if (this.fetchedReplyCountIds.has(ticket.ticketId)) continue;
      this.fetchedReplyCountIds.add(ticket.ticketId);
      this.supportService
        .getTicketReplyCount(ticket.ticketId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            this.ticketReplyCounts.set(ticket.ticketId, response.replyCount);
          },
          error: () => {
            // Allow retry on next poll cycle
            this.fetchedReplyCountIds.delete(ticket.ticketId);
          },
        });
    }
  }

  private mergeManualTickets(incoming: ManualTicket[]) {
    const incomingIds = new Set(incoming.map(t => t.ticketId));
    // Remove tickets no longer present
    this.manualTickets = this.manualTickets.filter(t => incomingIds.has(t.ticketId));
    // Update existing and add new
    for (const item of incoming) {
      const idx = this.manualTickets.findIndex(t => t.ticketId === item.ticketId);
      if (idx >= 0) {
        this.manualTickets[idx] = item;
      } else {
        this.manualTickets.push(item);
      }
    }
  }

  loadAutoModeState() {
    this.supportService.getAutoModeState().subscribe({
      next: res => {
        this.autoModeState = res;
      },
      error: () => {},
    });
  }

  toggleAutoMode() {
    if (this.autoModeToggleInFlight) return;
    this.autoModeToggleInFlight = true;
    const newEnabled = !this.autoModeState.enabled;
    this.supportService.toggleAutoMode(newEnabled).subscribe({
      next: () => {
        this.autoModeState.enabled = newEnabled;
        this.autoModeToggleInFlight = false;
        this.notification.success(`Auto mode ${newEnabled ? 'enabled' : 'disabled'}`);
      },
      error: () => {
        this.autoModeToggleInFlight = false;
        this.notification.error('Failed to toggle auto mode');
      },
    });
  }

  performAction(ticketId: number, action: 'solve' | 'close' | 'spam', closeModalOnSuccess = false) {
    if (this.ticketActionsInFlight.has(ticketId)) return;
    if (action === 'spam' && !confirm('Mark this ticket as spam?')) return;
    this.ticketActionsInFlight.add(ticketId);

    const actionMap = {
      solve: this.supportService.solveTicket(ticketId),
      close: this.supportService.closeTicket(ticketId),
      spam: this.supportService.spamTicket(ticketId),
    };

    actionMap[action].subscribe({
      next: res => {
        this.ticketActionsInFlight.delete(ticketId);
        this.notification.success(res.message);
        this.loadManualTickets();
        if (closeModalOnSuccess) this.closeTicketDetail();
      },
      error: () => {
        this.ticketActionsInFlight.delete(ticketId);
        this.notification.error(`Failed to ${action} ticket #${ticketId}`);
      },
    });
  }

  // ─── Autoclose Tickets ──────────────────────────────────────────

  onAutocloseDaysChange() {
    this.autoclosePage = 1;
    this.loadAutocloseTickets();
  }

  loadAutocloseTickets() {
    if (this.autocloseTickets.length === 0) {
      this.autocloseLoading = true;
    }
    const offset = (this.autoclosePage - 1) * this.autoclosePageSize;
    this.supportService
      .getAutocloseTickets({
        days: this.autocloseDays,
        limit: this.autoclosePageSize,
        offset,
      })
      .subscribe({
        next: res => {
          this.autocloseTickets = res.items;
          this.autocloseTotal = res.total;
          this.autocloseLoading = false;
          this.recomputeFilteredTickets();
        },
        error: () => {
          this.notification.error('Failed to load autoclose tickets');
          this.autocloseLoading = false;
        },
      });
  }

  get autoclosePageCount(): number {
    return Math.ceil(this.autocloseTotal / this.autoclosePageSize) || 1;
  }

  goToAutoclosePage(page: number) {
    if (page < 1 || page > this.autoclosePageCount) return;
    this.autoclosePage = page;
    this.loadAutocloseTickets();
  }

  // ─── Closed Manual Tickets ─────────────────────────────────────

  onClosedDaysChange() {
    this.closedPage = 1;
    this.loadClosedTickets();
  }

  loadClosedTickets() {
    if (this.closedTickets.length === 0) {
      this.closedLoading = true;
    }
    const offset = (this.closedPage - 1) * this.closedPageSize;
    this.supportService
      .getClosedManualTickets({
        days: this.closedDays,
        limit: this.closedPageSize,
        offset,
      })
      .subscribe({
        next: res => {
          this.closedTickets = res.items;
          this.closedTotal = res.total;
          this.closedLoading = false;
          this.recomputeFilteredTickets();
        },
        error: () => {
          this.notification.error('Failed to load closed tickets');
          this.closedLoading = false;
        },
      });
  }

  get closedPageCount(): number {
    return Math.ceil(this.closedTotal / this.closedPageSize) || 1;
  }

  goToClosedPage(page: number) {
    if (page < 1 || page > this.closedPageCount) return;
    this.closedPage = page;
    this.loadClosedTickets();
  }

  // ─── Ticket Detail ──────────────────────────────────────────────

  openTicketDetail(ticketId: number) {
    this.ticketDetailLoading = true;
    this.showTicketDetail = true;
    this.ticketDetailTab = 'conversation';
    this.ticketDetailIsManual = this.manualTickets.some(t => t.ticketId === ticketId);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ticket: ticketId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.supportService.getTicketThreadDetail(ticketId).subscribe({
      next: res => {
        this.ticketDetail = res;
        this.cachedReversedConversations = res.ticket?.conversations ? [...res.ticket.conversations].reverse() : [];
        this.ticketDetailLoading = false;
      },
      error: () => {
        this.notification.error('Failed to load ticket detail');
        this.ticketDetailLoading = false;
        this.showTicketDetail = false;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { ticket: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      },
    });
  }

  closeTicketDetail() {
    this.showTicketDetail = false;
    this.ticketDetail = null;
    this.feedbackExpanded = false;
    this.reviewRevisionComment = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ticket: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // ─── Cases CRUD ─────────────────────────────────────────────────

  loadCases() {
    this.casesLoading = true;
    this.supportService.getCases().subscribe({
      next: res => {
        this.cases = res.items;
        this.casesLoading = false;
      },
      error: () => {
        this.notification.error('Failed to load cases');
        this.casesLoading = false;
      },
    });
  }

  openCaseForm(editCase?: SupportCase) {
    this.editingCase = editCase || null;
    if (editCase) {
      this.caseForm.patchValue({
        name: editCase.name,
        instructions: editCase.instructions,
        autoclose: editCase.autoclose,
        sortOrder: editCase.sortOrder,
      });
    } else {
      this.caseForm.reset({ name: '', instructions: '', autoclose: false, sortOrder: 0 });
    }
    this.showCaseForm = true;
    setTimeout(() => {
      const el = this.instructionsTextarea?.nativeElement;
      if (el) {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
        el.style.overflowY = el.scrollHeight > el.clientHeight ? 'auto' : 'hidden';
      }
    });
  }

  closeCaseForm() {
    this.showCaseForm = false;
    this.editingCase = null;
  }

  saveCase() {
    if (this.caseForm.invalid) return;

    const data = this.caseForm.value;

    if (this.editingCase) {
      this.supportService.updateCase(this.editingCase.id, data).subscribe({
        next: () => {
          this.notification.success('Case updated');
          this.closeCaseForm();
          this.loadCases();
        },
        error: () => this.notification.error('Failed to update case'),
      });
    } else {
      this.supportService.createCase(data).subscribe({
        next: () => {
          this.notification.success('Case created');
          this.closeCaseForm();
          this.loadCases();
        },
        error: () => this.notification.error('Failed to create case'),
      });
    }
  }

  deleteCase(c: SupportCase) {
    if (!confirm(`Delete case "${c.name}"?`)) return;

    this.supportService.deleteCase(c.id).subscribe({
      next: () => {
        this.notification.success('Case deleted');
        this.loadCases();
      },
      error: () => this.notification.error('Failed to delete case'),
    });
  }

  toggleAutoclose(c: SupportCase) {
    this.supportService.toggleAutoclose(c.id, !c.autoclose).subscribe({
      next: updated => {
        const idx = this.cases.findIndex(x => x.id === c.id);
        if (idx >= 0) this.cases[idx] = updated;
      },
      error: () => this.notification.error('Failed to toggle autoclose'),
    });
  }

  openCaseHistory(c: SupportCase) {
    this.caseHistoryName = c.name;
    this.caseHistory = [];
    this.showCaseHistory = true;
    this.supportService.getCaseHistory(c.id).subscribe({
      next: res => {
        if (this.showCaseHistory) this.caseHistory = res.items;
      },
      error: () => this.notification.error('Failed to load case history'),
    });
  }

  closeCaseHistory() {
    this.showCaseHistory = false;
    this.caseHistory = [];
  }

  // ─── Prompt Config ────────────────────────────────────────────────

  loadPromptConfig() {
    this.promptConfigLoading = true;
    this.supportService.getPromptConfig().subscribe({
      next: res => {
        this.promptConfigs = res.items;
        this.promptConfigLoading = false;
      },
      error: () => {
        this.notification.error('Failed to load prompt config');
        this.promptConfigLoading = false;
      },
    });
  }

  savePromptConfig(pc: PromptConfig) {
    if (this.promptConfigSaving.has(pc.key)) return;
    this.promptConfigSaving.add(pc.key);

    this.supportService.updatePromptConfig(pc.key, pc.content).subscribe({
      next: updated => {
        const idx = this.promptConfigs.findIndex(p => p.key === pc.key);
        if (idx >= 0) this.promptConfigs[idx] = updated;
        this.promptConfigSaving.delete(pc.key);
        this.notification.success('Prompt config saved');
      },
      error: () => {
        this.promptConfigSaving.delete(pc.key);
        this.notification.error('Failed to save prompt config');
      },
    });
  }

  openPromptConfigHistory(pc: PromptConfig) {
    this.caseHistoryName = pc.key === 'prefix' ? 'Core Instructions' : 'Additional Notes';
    this.caseHistory = [];
    this.showCaseHistory = true;
    this.supportService.getPromptConfigHistory(pc.key).subscribe({
      next: res => {
        if (this.showCaseHistory) this.caseHistory = res.items;
      },
      error: () => this.notification.error('Failed to load prompt config history'),
    });
  }

  promptConfigLabel(key: string): string {
    return key === 'prefix' ? 'Core Instructions' : 'Additional Notes';
  }

  // ─── Retry ─────────────────────────────────────────────────────

  retryTicket(ticketId: number) {
    if (this.ticketActionsInFlight.has(ticketId)) return;
    this.ticketActionsInFlight.add(ticketId);

    this.supportService.retryTicket(ticketId).subscribe({
      next: () => {
        this.ticketActionsInFlight.delete(ticketId);
        this.notification.success('Ticket re-evaluated');
        this.loadManualTickets();
        if (this.showTicketDetail && this.ticketDetail?.ticketId === ticketId) {
          this.refreshTicketDetail(ticketId);
        }
      },
      error: () => {
        this.ticketActionsInFlight.delete(ticketId);
        this.notification.error('Failed to re-evaluate ticket');
      },
    });
  }

  private refreshTicketDetail(ticketId: number) {
    this.ticketDetailLoading = true;
    this.supportService
      .getTicketThreadDetail(ticketId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          if (this.showTicketDetail && this.ticketDetail?.ticketId === ticketId) {
            this.ticketDetail = res;
            this.cachedReversedConversations = res.ticket?.conversations ? [...res.ticket.conversations].reverse() : [];
          }
          this.ticketDetailLoading = false;
        },
        error: () => {
          this.notification.error('Failed to refresh ticket detail');
          this.ticketDetailLoading = false;
        },
      });
  }

  isTicketBusy(ticketId: number): boolean {
    return this.ticketActionsInFlight.has(ticketId);
  }

  // ─── TrackBy ──────────────────────────────────────────────────

  trackByTicketId(_index: number, item: { ticketId: number }): number {
    return item.ticketId;
  }

  trackByCaseId(_index: number, item: SupportCase): number {
    return item.id;
  }

  // ─── Helpers ────────────────────────────────────────────────────

  formatDate(ts: number | string | null): string {
    if (!ts) return '-';
    return this.datePipe.transform(new Date(ts), 'yyyy-MM-dd HH:mm') || '-';
  }

  formatConfidence(c: number | null | undefined): string {
    if (typeof c !== 'number' || isNaN(c)) return '-';
    return (c * 100).toFixed(0) + '%';
  }

  getActionBadgeClass(type: string | undefined): string {
    switch (type) {
      case 'reply':
        return 'badge bg-primary';
      case 'solve':
        return 'badge bg-success';
      case 'close':
        return 'badge bg-secondary';
      case 'spam':
        return 'badge bg-danger';
      case 'escalate':
        return 'badge bg-warning text-dark';
      case 'no_action':
        return 'badge bg-info';
      default:
        return 'badge bg-light text-dark';
    }
  }

  resetAutoMode() {
    if (!confirm('Reset auto mode? This will clear all manual tickets and recent auto ticket history.')) return;

    this.supportService.resetAutoMode().subscribe({
      next: res => {
        this.notification.success(`Reset complete: ${res.clearedManualTickets} manual tickets cleared`);
        this.loadManualTickets();
        this.loadAutoModeState();
      },
      error: () => this.notification.error('Failed to reset auto mode'),
    });
  }

  // ─── Edit Reply ──────────────────────────────────────────────

  startEditReply() {
    if (!this.canReviseTicketDetail()) return;
    // Deactivate feedback composer if active
    if (this.feedbackExpanded) {
      this.reviewRevisionComment = '';
      this.feedbackExpanded = false;
    }
    // Capture display height so textarea matches
    const displayEl = document.querySelector('.reply-editor__display') as HTMLElement;
    if (displayEl) {
      this.replyDisplayHeight = Math.max(displayEl.offsetHeight, 120);
    }
    this.editedReplyText = this.ticketDetail?.review?.decision?.action?.message || '';
    this.editingReply = true;
    // Auto-focus so clicking outside triggers blur
    setTimeout(() => {
      const textarea = document.querySelector('.reply-editor__textarea') as HTMLTextAreaElement;
      textarea?.focus();
    });
  }

  cancelEditReply() {
    this.editingReply = false;
    this.editedReplyText = '';
  }

  onReplyEditorBlur() {
    const original = this.ticketDetail?.review?.decision?.action?.message || '';
    if (this.editedReplyText === original) {
      this.editingReply = false;
      this.editedReplyText = '';
    }
  }

  saveEditedReply() {
    if (!this.ticketDetail || !this.editedReplyText.trim()) return;

    this.supportService.updateProposedReply(this.ticketDetail.ticketId, this.editedReplyText).subscribe({
      next: () => {
        if (this.ticketDetail?.review?.decision?.action) {
          this.ticketDetail.review.decision.action.message = this.editedReplyText;
        }
        this.editingReply = false;
        this.notification.success('Proposed reply updated');
      },
      error: () => this.notification.error('Failed to update proposed reply'),
    });
  }

  expandFeedback() {
    // Deactivate reply editor if active
    if (this.editingReply) {
      this.editingReply = false;
      this.editedReplyText = '';
    }
    this.feedbackExpanded = true;
  }

  onFeedbackBlur() {
    if (!this.reviewRevisionComment.trim() && !this.reviewRevisionSaving) {
      this.feedbackExpanded = false;
    }
  }

  blurFeedback() {
    const el = document.querySelector('.feedback-composer__input') as HTMLElement;
    el?.blur();
  }

  canReviseTicketDetail(): boolean {
    return !!this.ticketDetail && !!this.ticketDetail.review && this.activeTab === 'manual' && this.ticketDetailIsManual;
  }

  submitReviewRevision() {
    if (!this.canReviseTicketDetail()) return;

    const comment = this.reviewRevisionComment.trim();
    if (!comment || this.reviewRevisionSaving) return;

    this.reviewRevisionSaving = true;

    this.supportService.reviseTicketReview(this.ticketDetail.ticketId, comment).subscribe({
      next: res => {
        this.reviewRevisionSaving = false;
        this.reviewRevisionComment = '';
        this.editingReply = false;
        this.editedReplyText = '';
        this.feedbackExpanded = false;

        if (res.decision && this.ticketDetail?.review) {
          this.ticketDetail.review.decision = res.decision;
          this.ticketDetail.review.cachedAt = Date.now();
          this.updateManualTicketReview(this.ticketDetail.ticketId, res.decision);
        }

        this.recomputeFilteredTickets();
        this.notification.success('Ticket review updated');
      },
      error: () => {
        this.reviewRevisionSaving = false;
        this.notification.error('Failed to revise ticket review');
      },
    });
  }

  private updateManualTicketReview(ticketId: number, decision: SupportDecision) {
    const ticket = this.manualTickets.find(item => item.ticketId === ticketId);
    if (!ticket) return;

    ticket.decision = decision;
    ticket.reviewCachedAt = Date.now();
  }

  // ─── Conversation Image Toggle ──────────────────────────────
  toggleConversationImage(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG') {
      target.classList.toggle('img-expanded');
    }
  }

  // ─── User Data Helpers ──────────────────────────────────────

  isObject(val: any): boolean {
    return typeof val === 'object' && val !== null && !Array.isArray(val);
  }

  isArray(val: any): boolean {
    return Array.isArray(val);
  }

  formatKey(key: string): string {
    return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, s => s.toUpperCase());
  }

  flattenObject(obj: any): { key: string; value: any }[] {
    const result: { key: string; value: any }[] = [];
    const walk = (o: any) => {
      for (const k of Object.keys(o)) {
        const label = this.formatKey(k);
        const val = o[k];
        if (val !== null && val !== undefined && typeof val === 'object' && !Array.isArray(val)) {
          walk(val);
        } else if (Array.isArray(val)) {
          if (val.length === 0) {
            result.push({ key: label, value: null });
          } else if (typeof val[0] !== 'object') {
            result.push({ key: label, value: val.join(', ') });
          } else {
            result.push({ key: label, value: JSON.stringify(val) });
          }
        } else {
          result.push({ key: label, value: val });
        }
      }
    };
    walk(obj);
    return result;
  }

  getResolvedPhone(decision: SupportDecision | null | undefined): string | null {
    const phones = decision?.promptData?.ticket?.extractedContacts?.phoneNumbers;
    if (Array.isArray(phones) && phones.length > 0 && phones[0].normalized) {
      return phones[0].normalized;
    }
    return null;
  }

  freshdeskUrl(ticketId: number): string {
    return `https://moneysms.freshdesk.com/a/tickets/${ticketId}`;
  }
}
